import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Linking } from 'react-native'

import {
  CostMessagingModalContent,
  CoverageType,
  SessionsCoverageText,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { GetCustomerCopyFunc } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'

import {
  additionalHealthPlanSupportText,
  coachingForParents,
  coachingForParentsTitle,
  hpiMedsBaseDescription,
  sessionsCoverageMessages,
  therapyForChildren,
  therapyForChildrenTitle,
  therapyOrCoachingDescription,
  therapyOrCoachingTitle,
} from './util/DefaultCoverageTexts'
import { Link } from '../atoms/link/Link'

const amazonSessionsCoverageText: SessionsCoverageText = {
  title: <FormattedMessage {...sessionsCoverageMessages.coachingAndTherapySessionsTitle} />,
  description: <FormattedMessage {...sessionsCoverageMessages.eapAndHpiMixedDescription} />,
}

const highAlertEmergencyInfo = (
  <FormattedMessage
    defaultMessage='If you feel you’re at risk of harming yourself or someone else, please call <website>Resources for Living</website> at <phone>1-833-721-2323</phone> where counselors are available 24/7 to provide confidential support available to all Amazon employees and their household members.'
    description='Information for high alert contact resources'
    values={{
      website: (chunks: string) => (
        <Link
          text={chunks}
          onPress={() =>
            Linking.openURL('https://atoz.amazon.work/amazonbenefits/mentalhealth/provider/resource-for-living')
          }
        >
          {chunks}
        </Link>
      ),
      phone: (chunks: string) => (
        <Link text={chunks} underline onPress={() => Linking.openURL(`tel:18337212323`)}>
          {chunks}
        </Link>
      ),
    }}
  />
)

export const amazonHighAlertEmergencyInfo = highAlertEmergencyInfo

export const getAmazonCostMessagingModalText = (
  numberOfSessions: string,
  customerDisplayName: string,
): CostMessagingModalContent[] => [
  {
    title: coachingForParentsTitle,
    description: coachingForParents(numberOfSessions, customerDisplayName),
    contentType: ProgramNames.CoachingForParents,
  },
  {
    title: therapyForChildrenTitle,
    description: therapyForChildren(numberOfSessions, customerDisplayName),
    contentType: 'TherapyForChildren',
  },
  {
    title: therapyOrCoachingTitle,
    description: therapyOrCoachingDescription,
    contentType: 'TherapyOrCoaching',
  },
  { ...hpiMedsBaseDescription },
  { ...additionalHealthPlanSupportText },
]

const getCostMessagingModalText = (
  numberOfSessions: string,
  customerDisplayName: string,
): { [key in CoverageType]: () => CostMessagingModalContent[] } => {
  const costMessagingModalText = getAmazonCostMessagingModalText(numberOfSessions, customerDisplayName)
  return {
    [CoverageType.EAP_ONLY]: () => {
      return costMessagingModalText
    },
    [CoverageType.HPI_ONLY]: () => {
      return costMessagingModalText
    },
    [CoverageType.EAP_AND_HPI]: () => {
      return costMessagingModalText
    },
  }
}

export const amazon: GetCustomerCopyFunc = ({
  numberOfSessions,
  customerName,
  customerDisplayName,
  displayVisitsPerIssuePerYear,
}) => {
  const sessionCount =
    displayVisitsPerIssuePerYear != undefined ? displayVisitsPerIssuePerYear.toString() : numberOfSessions

  return {
    highAlertEmergencyInfo,
    costMessagingModalText: getCostMessagingModalText(
      displayVisitsPerIssuePerYear != undefined ? displayVisitsPerIssuePerYear.toString() : numberOfSessions || '',
      customerDisplayName ? customerDisplayName : customerName ?? '',
    ),
    sessionsCoverageText: amazonSessionsCoverageText,
    eligibilitySubtitleText: (_benefitEndDate: string) => (
      <FormattedMessage
        defaultMessage='Amazon offers {sessionCount} child therapy sessions for minors and {sessionCount} coaching for parents sessions for parents and caregivers per issue, per year at no cost.'
        description='Subtitle text for Amazon eligibility section describing available sessions for minors and parents'
        values={{ sessionCount }}
      />
    ),
    sessionInventoryText: ({
      displayVisitsPerIssuePerYear: numVisitsPerIssue,
      formatMessage,
      isMinor,
      remainingSessionCountByProgram,
    }) => {
      const sessionLimit = numVisitsPerIssue ?? (sessionCount ? parseInt(sessionCount) : null)

      if (sessionLimit === null) {
        return null
      }

      if (isMinor) {
        const remainingCount = remainingSessionCountByProgram?.therapy ?? sessionLimit
        return formatMessage(
          {
            defaultMessage: 'Therapy: {remainingCount} of {sessionLimit} available',
            description: 'Session inventory text for Amazon showing therapy session count for minors',
          },
          { remainingCount, sessionLimit },
        )
      }
      const remainingCount = remainingSessionCountByProgram?.coaching ?? sessionLimit
      return formatMessage(
        {
          defaultMessage: 'Coaching for parents: {remainingCount} of {sessionLimit} available',
          description: 'Session inventory text for Amazon showing coaching for parents session count',
        },
        { remainingCount, sessionLimit },
      )
    },
  }
}
